import { Injectable, Inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { CookieService } from 'ngx-cookie-service';
import { ConfigService } from '@app/shared/services/config/config.service';

const globalScope = (window || global) as any;
globalScope.dataLayer = globalScope.dataLayer || [];
globalScope.gtag = gtag;

// eslint-disable-next-line prefer-arrow/prefer-arrow-functions
function gtag() { globalScope.dataLayer.push(arguments); }

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService {
  private sendUrlOnRouteChangeToGoogleAnalyticsSubscription: Subscription;
  private readonly gtagUrl = 'https://www.googletagmanager.com/gtag/js';
  private readonly gaSourceScriptId = 'GaSourceScript';
  private persistentGtagConfig = {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    cookie_domain: this.configService.config.domain
  };

  constructor(
    private router: Router,
    private cookieService: CookieService,
    @Inject(DOCUMENT) private document: Document,
    private configService: ConfigService) { }

  loadGoogleAnalytics(): void {
    if (this.scriptsExistInDOM([this.gaSourceScriptId])) { return; }
    const sourceScript = this.document.createElement('script');
    sourceScript.setAttribute('async', 'true');
    sourceScript.setAttribute('id', this.gaSourceScriptId);
    sourceScript.setAttribute('src', `${this.gtagUrl}?id=${this.configService.config.googleAnalyticsKey}`);
    this.document.documentElement.firstChild.appendChild(sourceScript);

    globalScope.gtag('js', new Date());
    globalScope.gtag('set', this.persistentGtagConfig);
    globalScope.gtag('config', this.configService.config.googleAnalyticsKey);

    this.sendUrlOnRouteChangeToGoogleAnalytics();
  }

  unloadGoogleAnalytics(): void {
    if (this.scriptsExistInDOM([this.gaSourceScriptId])) {
      this.document.getElementById(this.gaSourceScriptId).remove();
    }
    const config = this.configService.config;
    this.advancedDeleteCookie('_ga', '/', config.domain, true, 'Strict');
    this.advancedDeleteCookie(`_gat_gtag_${config.googleAnalyticsKey.replace(/-/g, '_')}`, '/', config.domain, true, 'Strict');
    this.advancedDeleteCookie('_gid', '/', config.domain, true, 'Strict');
    globalScope.dataLayer = [];

    if (this.sendUrlOnRouteChangeToGoogleAnalyticsSubscription) {
      this.sendUrlOnRouteChangeToGoogleAnalyticsSubscription.unsubscribe();
      this.sendUrlOnRouteChangeToGoogleAnalyticsSubscription = null;
    }
  }

  sendCustomPageView(url: string) {
    if (!this.sendUrlOnRouteChangeToGoogleAnalyticsSubscription) {
      return;
    }
    // eslint-disable-next-line @typescript-eslint/naming-convention
    globalScope.gtag('config', this.configService.config.googleAnalyticsKey, { page_path: url });
  }

  private scriptsExistInDOM(scriptIds: string[]): boolean {
    for (const scriptId of scriptIds) {
      if (!this.document.getElementById(scriptId)) {
        return false;
      }
    }
    return true;
  }

  private sendUrlOnRouteChangeToGoogleAnalytics(): void {
    if (this.sendUrlOnRouteChangeToGoogleAnalyticsSubscription) {
      return;
    }

    this.sendUrlOnRouteChangeToGoogleAnalyticsSubscription = this.router.events
      .subscribe(event => {
        if (event instanceof NavigationEnd) {
          globalScope.gtag('config', this.configService.config.googleAnalyticsKey,
            // eslint-disable-next-line @typescript-eslint/naming-convention
            { page_path: event.urlAfterRedirects });
        }
      });
  }

  private advancedDeleteCookie(
    name: string,
    path?: string,
    domain?: string,
    secure?: boolean,
    sameSite: 'Lax' | 'None' | 'Strict' = 'Strict') {
    this.cookieService.set(name, '', new Date('Thu, 01 Jan 1970 00:00:01 GMT'), path, domain, secure, sameSite);
  }
}
